<template lang="pug">
el-dialog.event-banner-editor(
    :title="$t('Edit banner')"
    :visible.sync="dialogVisible"
    :before-close="confirmDiscard(() => moved)"
    :close-on-click-modal="false"
    width="920px"
    top="5vh"
    :append-to-body="true"
    @open="handleOpen"
    @closed="handleClosed"
)
    .editor-body
        .editor-hint {{ $t('Banner format hint') }}
        .cropper-wrap
            vue-cropper(
                v-if="dialogVisible && src"
                :key="cropperKey"
                ref="cropper"
                :src="src"
                :aspect-ratio="ratio"
                :ready="handleReady"
                :cropend="markMoved"
                :zoom="markMoved"
                :view-mode="1"
                drag-mode="crop"
                :auto-crop-area="1"
                :background="true"
                :rotatable="false"
                :zoomable="true"
                :movable="true"
                :check-orientation="false"
                :img-style="{ maxHeight: '58vh' }"
            )
    template(slot="footer")
        el-button(@click="reset" :disabled="!ready") {{ $t('Reset') }}
        .footer-spacer
        el-button(@click="close") {{ $t('Cancel') }}
        el-button(
            type="primary"
            icon="el-icon-fa-check fas"
            :disabled="!ready"
            @click="apply"
        ) {{ $t('Apply') }}
</template>

<i18n>
{
    "de": {
        "Edit banner": "Banner bearbeiten",
        "Banner format hint": "Der Banner wird im Format 16:9 zugeschnitten. Verschiebe/zoome den Ausschnitt und übernimm ihn. Das Originalbild bleibt erhalten.",
        "Reset": "Zurücksetzen",
        "Apply": "Übernehmen",
        "Cancel": "Abbrechen"
    }
}
</i18n>

<script>
import _ from 'lodash'
import VueCropper from 'vue-cropperjs'
import discardGuard from '@/mixins/discardGuard'

// Fester Banner-Standard: 16:9.
const BANNER_RATIO = 16 / 9

export default {
    name: 'EventBannerEditor',
    mixins: [discardGuard],
    components: { VueCropper },
    props: {
        visible: {
            type: Boolean,
            default: false,
        },
        // Der Banner: { url (Original-File), width, height, settings: { processing } }.
        image: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            ratio: BANNER_RATIO,
            ready: false,
            // true once the user has dragged or zoomed the cut-out
            moved: false,
            // Bei jedem Öffnen erhöht → frischer Cropper (kein alter Ausschnitt).
            cropperKey: 0,
        }
    },
    computed: {
        dialogVisible: {
            get() {
                return this.visible
            },
            set(value) {
                this.$emit('update:visible', value)
            },
        },
        // Immer das Original-File (ohne Crop-Parameter) bearbeiten.
        src() {
            return _.get(this.image, 'url', '') || ''
        },
    },
    methods: {
        getCropper() {
            const wrapper = this.$refs.cropper
            return wrapper ? wrapper.cropper : null
        },
        handleOpen() {
            this.ready = false
            this.moved = false
            this.cropperKey += 1
        },
        markMoved() {
            if (this.ready) this.moved = true
        },
        handleClosed() {
            this.ready = false
        },
        // Beim Laden einen bestehenden Zuschnitt (settings.processing) wiederherstellen,
        // damit der Nutzer ihn weiter anpassen kann statt bei Null zu starten.
        handleReady() {
            this.ready = true
            const cropper = this.getCropper()
            const processing = _.get(this.image, 'settings.processing')
            if (!cropper || !processing) return

            const img = cropper.getImageData()
            const naturalWidth = img.naturalWidth
            const naturalHeight = img.naturalHeight
            const w = parseFloat(processing.w)
            const h = parseFloat(processing.h)
            const fx = parseFloat(processing.x)
            const fy = parseFloat(processing.y)
            if (!w || !h || Number.isNaN(fx) || Number.isNaN(fy)) return

            cropper.setData({
                x: fx * naturalWidth - w / 2,
                y: fy * naturalHeight - h / 2,
                width: w,
                height: h,
            })
        },
        // Fokuspunkt-Crop aus den Cropper-Daten (natürliche Pixel) berechnen.
        // Spiegelt ImageCropperModal.getProcessingFromCropData.
        getProcessingFromCropData(data, originalWidth, originalHeight) {
            const { width, height, x, y } = data
            const fx = (x + width / 2) / originalWidth
            const fy = (y + height / 2) / originalHeight
            const originalAspectRatio = originalWidth / originalHeight
            const fz = originalAspectRatio > 1 ? originalHeight / height : originalWidth / width
            return {
                fit: 'crop',
                crop: 'focalpoint',
                x: fx,
                y: fy,
                z: fz,
                w: Math.round(width),
                h: Math.round(height),
            }
        },
        close() {
            this.dialogVisible = false
        },
        apply() {
            const cropper = this.getCropper()
            if (!cropper) return
            const data = cropper.getData(true)
            const img = cropper.getImageData()
            const processing = this.getProcessingFromCropData(data, img.naturalWidth, img.naturalHeight)
            this.$emit('crop', processing)
            this.close()
        },
        // Zuschnitt entfernen → Standard-16:9 (zentriert) bzw. Original ohne Fokus-Crop.
        reset() {
            this.$emit('reset')
            this.close()
        },
    },
}
</script>

<style lang="sass" scoped>
.editor-body
    min-height: 200px
    .editor-hint
        font-size: 13px
        color: #909399
        margin-bottom: 12px
    .cropper-wrap
        width: 100%
        max-height: 58vh
        background: #f5f7fa
        border-radius: 6px
        overflow: hidden
        ::v-deep img
            max-width: 100%
.footer-spacer
    display: inline-block
    width: 24px
</style>
